import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import CarList from "../../components/CarList";
import PageWrapper from "../../components/PageWrapper";
import SearchForm from "../../components/SearchForm";
import { Settings } from "../../context/SettingsContext/SettingsContext";
import {
  Car,
  Brand,
  FuelType,
  Category,
  Model,
  Pagination as PaginationProps,
  SearchParams,
  fetchBrands,
  fetchModels,
  fetchCars,
  fetchFuelTypes,
  fetchCategories,
  InteriorColor,
  InteriorType,
  Feature,
  fetchInteriorColors,
  fetchInteriorTypes,
  fetchFeatures,
} from "../../services/mobile";
import Box from "../../styles/Box";
import Button from "../../styles/Button";
import Label from "../../styles/Label";
import Paragraph from "../../styles/Paragraph";
import Select from "../../styles/Select";
import theme from "../../styles/theme";
import { ReactComponent as LoadingIcon } from "../../assets/svg/loading.svg";

const getSearchPrams = () => {
  const params = new URLSearchParams(window.location.search);

  const brand = params.get("brand");
  const model = params.get("model");

  return {
    brand,
    model,
  };
};

const Sidebar = styled.div`
  width: 100%;

  @media (min-width: ${theme.mediaQueries.tablet}px) {
    flex-basis: 340px;
    flex-shrink: 0;
    flex-grow: 0;
    margin-right: ${theme.unit * 4}px;
  }
`;

const Content = styled.div`
  width: 100%;
`;

export interface SearchState {
  cars?: Car[];
  brands?: Brand[];
  models?: Model[];
  fuelTypes?: FuelType[];
  interiorColors: InteriorColor[];
  interiorTypes: InteriorType[];
  features: Feature[];
  categories?: Category[];
  brandRef?: string | null;
  searchParams?: SearchParams | null;
  pagination?: PaginationProps | false;
  sort?: string;
  minConstructionYear?: number;
  maxConstructionYear?: number;
  lastSearchedParams?: SearchParams | null;
}

const initialSearchParams: SearchParams = {
  minConstructionYear: new Date().getFullYear() - 4,
  sort: "modificationTime_DESCENDING",
  brandRef: "",
  modelRef: "",
};

interface Props {
  settings: Settings;
}

const Search: FunctionComponent<Props> = ({ settings }) => {
  const location = useLocation<SearchState>();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [initializedQueryParams, setInitializedQueryParams] = useState(false);
  const [cars, setCars] = useState<Car[]>(location.state?.cars || []);
  const [brands, setBrands] = useState<Brand[]>(location.state?.brands || []);
  const [models, setModels] = useState<Model[]>(location.state?.models || []);
  const [fuelTypes, setFuelTypes] = useState<FuelType[]>(
    location.state?.fuelTypes || []
  );
  const [interiorColors, setInteriorColors] = useState<InteriorColor[]>(
    location.state?.interiorColors || []
  );
  const [interiorTypes, setInteriorTypes] = useState<InteriorType[]>(
    location.state?.interiorTypes || []
  );
  const [features, setFeatures] = useState<Feature[]>(
    location.state?.features || []
  );
  const [categories, setCategories] = useState<Category[]>(
    location.state?.categories || []
  );
  const [searchParams, setSearchParams] = useState<SearchParams | null>(
    location.state?.searchParams || initialSearchParams
  );
  const [pagination, setPagination] = useState<PaginationProps | false>(
    location.state?.pagination || false
  );
  const [brandRef, setBrandRef] = useState<string | null>(
    location.state?.brandRef || null
  );
  const [
    lastSearchedParams,
    setLastSearchedParams,
  ] = useState<SearchParams | null>(location.state?.lastSearchedParams || null);

  const navigateToCar = useCallback(
    (id) => () => {
      history.replace(location.pathname, {
        cars,
        brands,
        models,
        fuelTypes,
        categories,
        brandRef,
        searchParams,
        pagination,
        lastSearchedParams,
      });
      history.push(`/car?id=${id}`);
    },
    [
      history,
      location,
      cars,
      brands,
      models,
      fuelTypes,
      categories,
      brandRef,
      searchParams,
      pagination,
      lastSearchedParams,
    ]
  );
  useEffect(() => {
    fetchBrands().then((results) => {
      if (results) {
        setBrands(
          results.filter((item) => settings.brands.indexOf(item.name) !== -1)
        );
      }
    });
  }, [settings]);
  useEffect(() => {
    if (brandRef) {
      fetchModels(brandRef).then((response) => {
        if (response !== false) {
          setModels(response.models);
        }
      });
    }
  }, [brandRef]);
  useEffect(() => {
    fetchFuelTypes().then((results) => {
      if (results) {
        setFuelTypes(results);
      }
    });
  }, [settings]);
  useEffect(() => {
    fetchInteriorColors().then((results) => {
      if (results) {
        setInteriorColors(results);
      }
    });
  }, [settings]);
  useEffect(() => {
    fetchInteriorTypes().then((results) => {
      if (results) {
        setInteriorTypes(results);
      }
    });
  }, [settings]);
  useEffect(() => {
    fetchFeatures().then((results) => {
      if (results) {
        setFeatures(results);
      }
    });
  }, [settings]);
  useEffect(() => {
    fetchCategories().then((results) => {
      if (results) {
        setCategories(results);
      }
    });
  }, [settings]);
  useEffect(() => {
    const initialize = async () => {
      if (
        !searchParams ||
        JSON.stringify(lastSearchedParams) === JSON.stringify(searchParams)
      ) {
        return;
      }
      if (!searchParams.brandRef || !searchParams.modelRef) {
        setCars([]);
        return;
      }
      setLoading(true);
      try {
        const response = await fetchCars(searchParams, settings);
        setLastSearchedParams(searchParams);
        if (response !== false && response.cars) {
          console.log("setting cars to", response.cars);
          setCars(response.cars);
          setPagination(response.pagination);
        } else {
          setCars([]);
          setPagination(false);
        }
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    initialize();
  }, [
    searchParams,
    lastSearchedParams,
    setLastSearchedParams,
    setLoading,
    settings,
  ]);

  const onSubmit = useCallback(
    async (values: SearchParams) => {
      setSearchParams({
        ...searchParams,
        ...values,
      });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    if (initializedQueryParams) {
      return;
    }
    const params = getSearchPrams();
    const brand = brands.find((item) => item.name === params.brand);
    const model = models.find((item) => item.name === params.model);

    if (brand && !model) {
      onSubmit({
        brandRef: brand.ref,
      });
    } else if (brand && model) {
      onSubmit({
        brandRef: brand.ref,
        modelRef: model.ref,
      });
    }

    if (brands.length > 0 && models.length > 0) {
      setInitializedQueryParams(true);
    }
  }, [
    brands,
    models,
    onSubmit,
    setInitializedQueryParams,
    initializedQueryParams,
  ]);

  const setSort = useCallback(
    (event: React.FormEvent<HTMLSelectElement>) => {
      setSearchParams({
        ...searchParams,
        sort: event.currentTarget.value,
      });
    },
    [setSearchParams, searchParams]
  );

  const moreCars = useCallback(async () => {
    if (!searchParams || !pagination) {
      return;
    }
    const response = await fetchCars(
      {
        ...searchParams,
        page: pagination.currentPage + 1,
      },
      settings
    );
    if (response !== false) {
      setCars([...cars, ...response.cars]);
      setPagination(response.pagination);
    }
  }, [searchParams, pagination, cars, setCars, settings]);

  return (
    <PageWrapper>
      <Sidebar>
        <SearchForm
          searchState={{
            brandRef,
            brands,
            fuelTypes,
            interiorColors,
            interiorTypes,
            features,
            categories,
            cars,
            models,
            pagination,
            searchParams,
          }}
          onSubmit={onSubmit}
          setBrandRef={setBrandRef}
        />
      </Sidebar>
      <Content>
        <Label htmlFor="sort-value">Sorteren op:</Label>
        <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
        <Select id="sort-value" onChange={setSort}>
          <option value="modificationTime_DESCENDING">
            Nieuw geplaatste modellen eerst
          </option>
          <option value="modificationTime_ASCENDING">
            Nieuw geplaatste modellen laatst
          </option>
          <option value="price_ASCENDING">Prijs oplopend</option>
          <option value="price_DESCENDING">Prijs aflopend</option>
        </Select>
        {loading && (
          <Box centered>
            <LoadingIcon />
          </Box>
        )}
        {!loading && cars.length === 0 && (
          <>
            <Paragraph>
              {!searchParams?.brandRef || !searchParams?.modelRef
                ? "Kies een merk en model"
                : "Geen resultaten gevonden"}
            </Paragraph>
          </>
        )}
        {!loading && <CarList onClickCar={navigateToCar} cars={cars} />}

        {!loading &&
          pagination &&
          pagination.currentPage < pagination.maxPages && (
            <Box centered>
              <Button onClick={moreCars}>Meer zoekresultaten tonen</Button>
            </Box>
          )}
      </Content>
    </PageWrapper>
  );
};

export default Search;
